import { useState } from "react";
import { toast } from "react-toastify";

// Define interfaces for cycle breakdown data
export interface CycleBreakdownPrintData {
  date: string; // Main date (saveToDate)
  cycles: Array<{
    cycleId: string;
    displayName: string;
    startTime: string;
    endTime: string;
  }>;
  items: Array<{
    itemId: string;
    displayName: string;
    portionSize: string;
    prodPlanQty: number;
    classFilterTag?: string;
    quantities?: Record<string, number>;
  }>;
  productQuantities: Record<string, Record<string, number>>;
  totalPlanned: number;
  restaurantId: string;
  restaurantName?: string;
  // NEW: Optional source date for template/source mode
  sourceDate?: string;
  // NEW: Optional flags for template/source mode
  isTemplateMode?: boolean;
  isSourceMode?: boolean;
}

const useCycleBreakdownPrint = () => {
  const [isPrinting, setIsPrinting] = useState(false);

  const generatePrintableContent = (data: CycleBreakdownPrintData): string => {
    const { 
      date, 
      cycles, 
      items, 
      productQuantities,
      totalPlanned,
      sourceDate,
      isTemplateMode = false,
      isSourceMode = false,
    } = data;

    // Filter items that have planned quantity > 0
    const plannedItems = items.filter(item => item.prodPlanQty > 0);
    
    // Group items by tier for sorting
    const itemsByTier: Record<string, typeof plannedItems> = {
      platinum: [],
      gold: [],
      bronze: [],
      "no-tier": [],
    };

    plannedItems.forEach(item => {
      const tier = item.classFilterTag || "no-tier";
      if (itemsByTier[tier]) {
        itemsByTier[tier].push(item);
      } else {
        itemsByTier["no-tier"].push(item);
      }
    });

    // Sort items within each tier by display name
    Object.keys(itemsByTier).forEach(tier => {
      itemsByTier[tier].sort((a, b) => a.displayName.localeCompare(b.displayName));
    });

    const formattedDate = new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Format source date if available
    const formattedSourceDate = sourceDate 
      ? new Date(sourceDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null;

    // Create header based on mode
    let headerTitle = "PRODUCTION CYCLE BREAKDOWN";
    let dateInfo = formattedDate;
    
    if (isSourceMode && sourceDate) {
      headerTitle = "PRODUCTION CYCLE TEMPLATE BREAKDOWN";
      dateInfo = `
        <div style="display: flex; flex-direction: column; gap: 4px; margin-top: 5px;">
          <div> ${formattedSourceDate}</div>
       
        </div>
      `;
    } else if (isTemplateMode && sourceDate) {
      headerTitle = "PRODUCTION CYCLE TEMPLATE BREAKDOWN";
      dateInfo = `
        <div style="display: flex; flex-direction: column; gap: 4px; margin-top: 5px;">
          <div>${formattedDate}</div>
         
        </div>
      `;
    }

    // Helper function to get quantity for a product in a cycle
    const getQuantity = (itemId: string, portionSize: string, cycleId: string): number => {
      const uniqueKey = `${itemId}_${portionSize}`;
      return productQuantities[uniqueKey]?.[cycleId] || 0;
    };

    // Helper function to get total allocated for a product
    const getProductTotal = (itemId: string, portionSize: string): number => {
      const uniqueKey = `${itemId}_${portionSize}`;
      const cycleData = productQuantities[uniqueKey] || {};
      return Object.values(cycleData).reduce((sum, qty) => sum + (qty || 0), 0);
    };

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cycle Breakdown - ${date}</title>
          <meta charset="UTF-8">
          <style>
            /* Black and white styling for printing */
            @media print {
              @page {
                size: A4 portrait;
                margin: 10mm;
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 9pt;
                line-height: 1.2;
                color: #000 !important;
                background: white !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              /* Force black and white */
              * {
                color: #000 !important;
                background: white !important;
                border-color: #000 !important;
              }
              
              /* Remove all colors */
              th {
                background: #f0f0f0 !important;
                color: #000 !important;
              }
              
              /* Zebra striping with light grays */
              tr:nth-child(even) {
                background: #f9f9f9 !important;
              }
              
              tr:nth-child(odd) {
                background: white !important;
              }
              
              .total-row {
                background: #f0f0f0 !important;
                font-weight: bold;
              }
              
              .source-indicator {
                background: #f0f0f0 !important;
                border: 1px solid #000 !important;
              }
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 9pt;
              line-height: 1.2;
              color: #000;
              margin: 0;
              padding: 10px;
              background: white;
            }
            
            .header {
              text-align: center;
              margin-bottom: 10px;
              padding-bottom: 8px;
              border-bottom: 2px solid #000;
            }
            
            .header h1 {
              margin: 0 0 5px 0;
              font-size: 16pt;
              font-weight: bold;
              text-transform: uppercase;
            }
            
            .date-info {
              background-color: #f5f5f5;
              padding: 8px 12px;
              border-radius: 4px;
              margin-top: 5px;
              width: 100%;
              max-width: 500px;
              margin-left: auto;
              margin-right: auto;
            }
            
            .date-info-item {
              display: flex;
              justify-content: space-between;
              margin: 2px 0;
            }
            
            .date-info-label {
              font-weight: bold;
              color: #555;
            }
            
            .date-info-value {
              color: #000;
            }
            
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
              font-size: 8pt;
            }
            
            .info-box {
              padding: 3px 6px;
              background-color: #f5f5f5;
              border-radius: 2px;
              border: 1px solid #ddd;
            }
            
            .source-indicator {
              background-color: #f0f0f0;
              border: 1px solid #ddd;
              color: #333;
              padding: 6px 10px;
              border-radius: 4px;
              margin: 10px auto;
              text-align: center;
              font-size: 9pt;
              font-weight: bold;
              width: fit-content;
            }
            
            .total-section {
              text-align: center;
              font-weight: bold;
              margin: 8px 0;
              padding: 6px;
              background-color: #f0f0f0;
              border: 1px solid #ddd;
              border-radius: 3px;
              font-size: 10pt;
            }
            
            .table-container {
              width: 100%;
              overflow: hidden;
              margin-top: 10px;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 8pt;
              table-layout: fixed;
            }
            
            th {
              background: #f0f0f0;
              color: #000;
              font-weight: bold;
              padding: 5px 3px;
              text-align: left;
              border: 1px solid #000;
              vertical-align: middle;
            }
            
            td {
              padding: 4px 3px;
              border: 1px solid #000;
              vertical-align: top;
              word-wrap: break-word;
            }
            
            /* Column widths */
            .product-column {
              width: 30%;
            }
            
            .cycle-column {
              width: ${cycles.length > 0 ? `${70 / (cycles.length + 2)}%` : '10%'};
              text-align: center;
            }
            
            .planned-column {
              width: 8%;
              text-align: center;
            }
            
            .status-column {
              width: 8%;
              text-align: center;
            }
            
            /* Cycle header styling */
            .cycle-header {
              font-weight: bold;
              font-size: 8pt;
            }
            
            .cycle-time {
              font-size: 7pt;
              color: #666;
            }
            
            /* Product styling */
            .product-name {
              font-weight: 500;
            }
            
            .product-details {
              font-size: 7pt;
              color: #666;
              margin-top: 2px;
            }
            
            .tier-indicator {
              display: inline-block;
              width: 8px;
              height: 8px;
              border-radius: 50%;
              margin-right: 4px;
              vertical-align: middle;
            }
            
            .tier-platinum { background: #aaa; }
            .tier-gold { background: #777; }
            .tier-bronze { background: #444; }
            .tier-none { background: #ccc; }
            
            /* Quantity cells */
            .quantity-cell {
              text-align: center;
              font-weight: 500;
            }
            
            /* Variance status */
            .status-complete {
              color: #000;
              font-weight: bold;
            }
            
            .status-remaining {
              color: #666;
            }
            
            .status-over {
              color: #000;
              font-weight: bold;
            }
            
            /* Totals row */
            .total-row td {
              font-weight: bold;
              background: #f0f0f0;
            }
            
            .cycle-total {
              text-align: center;
              font-weight: bold;
            }
            
            .footer {
              margin-top: 15px;
              padding-top: 5px;
              border-top: 1px solid #000;
              font-size: 7pt;
              color: #666;
              text-align: center;
            }
            
            .no-data {
              text-align: center;
              padding: 20px;
              font-style: italic;
              color: #666;
            }
            
            /* Page break handling */
            .page-break {
              page-break-before: always;
            }
            
            /* Compact layout */
            .compact {
              margin: 0;
              padding: 0;
            }
            
            @media print {
              .no-print {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${headerTitle}</h1>
            ${dateInfo}
           
          </div>
          
          <div class="total-section">
            Total Planned Production: ${totalPlanned} packs
          </div>
          
          <div class="table-container">
            ${
              plannedItems.length > 0 && cycles.length > 0
                ? `
                  <table>
                    <thead>
                      <tr>
                        <th class="product-column">PRODUCT</th>
                        ${cycles.map(cycle => `
                          <th class="cycle-column">
                            <div class="cycle-header">${cycle.displayName}</div>
                            <div class="cycle-time">${cycle.startTime} - ${cycle.endTime}</div>
                          </th>
                        `).join('')}
                        <th class="planned-column">PLANNED</th>
                        <th class="status-column">STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- Platinum Tier -->
                      ${itemsByTier.platinum && itemsByTier.platinum.length > 0 
                        ? itemsByTier.platinum.map(item => {
                            const allocatedTotal = getProductTotal(item.itemId, item.portionSize);
                            const variance = item.prodPlanQty - allocatedTotal;
                            const status = variance === 0 ? 'Complete' : variance > 0 ? `${variance} remaining` : `${Math.abs(variance)} over`;
                            
                            return `
                              <tr>
                                <td class="product-column">
                                  <div class="product-name">
                                    <span class="tier-indicator tier-platinum" title="Platinum"></span>
                                    ${item.displayName}
                                  </div>
                                 
                                </td>
                                ${cycles.map(cycle => {
                                  const qty = getQuantity(item.itemId, item.portionSize, cycle.cycleId);
                                  return `
                                    <td class="cycle-column quantity-cell">${qty || ''}</td>
                                  `;
                                }).join('')}
                                <td class="planned-column">${item.prodPlanQty}</td>
                                <td class="status-column ${
                                  variance === 0 ? 'status-complete' : 
                                  variance > 0 ? 'status-remaining' : 'status-over'
                                }">
                                  ${status}
                                </td>
                              </tr>
                            `;
                          }).join('')
                        : ''
                      }
                      
                      <!-- Gold Tier -->
                      ${itemsByTier.gold && itemsByTier.gold.length > 0 
                        ? itemsByTier.gold.map(item => {
                            const allocatedTotal = getProductTotal(item.itemId, item.portionSize);
                            const variance = item.prodPlanQty - allocatedTotal;
                            const status = variance === 0 ? 'Complete' : variance > 0 ? `${variance} remaining` : `${Math.abs(variance)} over`;
                            
                            return `
                              <tr>
                                <td class="product-column">
                                  <div class="product-name">
                                    <span class="tier-indicator tier-gold" title="Gold"></span>
                                    ${item.displayName}
                                  </div>
                                 
                                </td>
                                ${cycles.map(cycle => {
                                  const qty = getQuantity(item.itemId, item.portionSize, cycle.cycleId);
                                  return `
                                    <td class="cycle-column quantity-cell">${qty || ''}</td>
                                  `;
                                }).join('')}
                                <td class="planned-column">${item.prodPlanQty}</td>
                                <td class="status-column ${
                                  variance === 0 ? 'status-complete' : 
                                  variance > 0 ? 'status-remaining' : 'status-over'
                                }">
                                  ${status}
                                </td>
                              </tr>
                            `;
                          }).join('')
                        : ''
                      }
                      
                      <!-- Bronze Tier -->
                      ${itemsByTier.bronze && itemsByTier.bronze.length > 0 
                        ? itemsByTier.bronze.map(item => {
                            const allocatedTotal = getProductTotal(item.itemId, item.portionSize);
                            const variance = item.prodPlanQty - allocatedTotal;
                            const status = variance === 0 ? 'Complete' : variance > 0 ? `${variance} remaining` : `${Math.abs(variance)} over`;
                            
                            return `
                              <tr>
                                <td class="product-column">
                                  <div class="product-name">
                                    <span class="tier-indicator tier-bronze" title="Bronze"></span>
                                    ${item.displayName}
                                  </div>
                                 
                                </td>
                                ${cycles.map(cycle => {
                                  const qty = getQuantity(item.itemId, item.portionSize, cycle.cycleId);
                                  return `
                                    <td class="cycle-column quantity-cell">${qty || ''}</td>
                                  `;
                                }).join('')}
                                <td class="planned-column">${item.prodPlanQty}</td>
                                <td class="status-column ${
                                  variance === 0 ? 'status-complete' : 
                                  variance > 0 ? 'status-remaining' : 'status-over'
                                }">
                                  ${status}
                                </td>
                              </tr>
                            `;
                          }).join('')
                        : ''
                      }
                      
                      <!-- No Tier -->
                      ${itemsByTier["no-tier"] && itemsByTier["no-tier"].length > 0 
                        ? itemsByTier["no-tier"].map(item => {
                            const allocatedTotal = getProductTotal(item.itemId, item.portionSize);
                            const variance = item.prodPlanQty - allocatedTotal;
                            const status = variance === 0 ? 'Complete' : variance > 0 ? `${variance} remaining` : `${Math.abs(variance)} over`;
                            
                            return `
                              <tr>
                                <td class="product-column">
                                  <div class="product-name">
                                    <span class="tier-indicator tier-none" title="No Tier"></span>
                                    ${item.displayName}
                                  </div>
                                
                                </td>
                                ${cycles.map(cycle => {
                                  const qty = getQuantity(item.itemId, item.portionSize, cycle.cycleId);
                                  return `
                                    <td class="cycle-column quantity-cell">${qty || ''}</td>
                                  `;
                                }).join('')}
                                <td class="planned-column">${item.prodPlanQty}</td>
                                <td class="status-column ${
                                  variance === 0 ? 'status-complete' : 
                                  variance > 0 ? 'status-remaining' : 'status-over'
                                }">
                                  ${status}
                                </td>
                              </tr>
                            `;
                          }).join('')
                        : ''
                      }
                      
                      <!-- Totals Row -->
                      <tr class="total-row">
                        <td class="product-column"><strong>CYCLE TOTALS</strong></td>
                        ${cycles.map(cycle => {
                          // Calculate cycle total
                          let cycleTotal = 0;
                          Object.keys(productQuantities).forEach(key => {
                            cycleTotal += productQuantities[key]?.[cycle.cycleId] || 0;
                          });
                          return `
                            <td class="cycle-column cycle-total">${cycleTotal}</td>
                          `;
                        }).join('')}
                        <td class="planned-column">${totalPlanned}</td>
                        <td class="status-column">
                          ${
                            (() => {
                              const totalAllocated = Object.values(productQuantities).reduce((sum, cycleData) => {
                                if (!cycleData) return sum;
                                return sum + Object.values(cycleData).reduce(
                                  (cycleSum, qty) => cycleSum + (qty || 0),
                                  0
                                );
                              }, 0);
                              const totalVariance = totalPlanned - totalAllocated;
                              return totalVariance === 0 ? 'Complete' : `${totalVariance} left`;
                            })()
                          }
                        </td>
                      </tr>
                    </tbody>
                  </table>
                `
                : `
                  <div class="no-data">
                    ${cycles.length === 0 ? 'No cycles available' : plannedItems.length === 0 ? 'No products with planned quantities' : 'No data available'}
                  </div>
                `
            }
          </div>
          
          <div class="footer">
            <div>Generated: ${new Date().toLocaleString()}</div>
          
          </div>
          
          <script>
            // Auto-print when page loads
            window.onload = function() {
              setTimeout(function() {
                window.print();
                // Close window after print dialog appears
                setTimeout(function() {
                  window.close();
                }, 100);
              }, 100);
            };
          </script>
        </body>
      </html>
    `;
  };

  const printCycleBreakdown = async (data: CycleBreakdownPrintData) => {
    setIsPrinting(true);
    
    try {
      const printContent = generatePrintableContent(data);
      
      // Create a new window for printing
      const printWindow = window.open("", "_blank", "width=1123,height=794"); // A4 landscape dimensions
      if (!printWindow) {
        throw new Error("Please allow popups to print.");
      }
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Focus the window
      printWindow.focus();
      
      // Set a timeout to reset printing state
      setTimeout(() => {
        setIsPrinting(false);
      }, 3000);
      
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to print. Please check your popup settings.");
      setIsPrinting(false);
    }
  };

  return {
    printCycleBreakdown,
    isPrinting,
  };
};

export default useCycleBreakdownPrint;